import React, { useState, useCallback, useRef, useEffect } from "react";
import { Autocomplete } from "@react-google-maps/api";

interface RouteDirectionsWithAutocompleteProps {
  map: google.maps.Map | null;
  onRouteCalculated?: (distance: string, duration: string) => void;
  restaurants?: Array<{
    dbID: string;
    name: string;
    storeLat: number;
    storeLong: number;
    [key: string]: any;
  }>;
  onNearbyRestaurantsFound?: (restaurantIds: string[]) => void;
  onRouteDistancesCalculated?: (distances: Map<string, number>) => void;
  initialRouteDestination?: any | null; // using any to avoid importing Restaurant if not needed, or better define it
  userLocation?: { latitude: number; longitude: number } | null;
}

const RouteDirectionsWithAutocomplete: React.FC<
  RouteDirectionsWithAutocompleteProps
> = ({
  map,
  onRouteCalculated,
  restaurants = [],
  onNearbyRestaurantsFound,
  onRouteDistancesCalculated,
  initialRouteDestination,
  userLocation,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [fromLocation, setFromLocation] = useState("");
    const [toLocation, setToLocation] = useState("");
    const [directionsResponse, setDirectionsResponse] =
      useState<google.maps.DirectionsResult | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [distance, setDistance] = useState<string>("");
    const [duration, setDuration] = useState<string>("");
    const [nearbyRestaurants, setNearbyRestaurants] = useState<string[]>([]);
    const [routeKey, setRouteKey] = useState<number>(0);
    const [showRoute, setShowRoute] = useState(false);
    const [hasAutoCalculated, setHasAutoCalculated] = useState(false);
    const maxDistanceKm = 10; // Fixed 10km from route

    useEffect(() => {
      if (initialRouteDestination && userLocation && !hasAutoCalculated) {
        setFromLocation(`${userLocation.latitude},${userLocation.longitude}`);
        setToLocation(`${initialRouteDestination.storeLat},${initialRouteDestination.storeLong}`);
      }
    }, [initialRouteDestination, userLocation, hasAutoCalculated]);

    const fromAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(
      null
    );
    const toAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(
      null
    );
    const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(
      null
    );
    const panelRef = useRef<HTMLDivElement | null>(null);

    const onFromLoad = (autocomplete: google.maps.places.Autocomplete) => {
      fromAutocompleteRef.current = autocomplete;
    };

    const onToLoad = (autocomplete: google.maps.places.Autocomplete) => {
      toAutocompleteRef.current = autocomplete;
    };

    const onFromPlaceChanged = () => {
      if (fromAutocompleteRef.current) {
        const place = fromAutocompleteRef.current.getPlace();
        // Check if place exists and has formatted_address
        if (place && place.formatted_address) {
          setFromLocation(place.formatted_address);
        } else if (place && place.name) {
          // Fallback to place name if formatted_address is not available
          setFromLocation(place.name);
        }
        // If neither exists, keep the current input value (user typed text)
      }
    };

    const onToPlaceChanged = () => {
      if (toAutocompleteRef.current) {
        const place = toAutocompleteRef.current.getPlace();
        // Check if place exists and has formatted_address
        if (place && place.formatted_address) {
          setToLocation(place.formatted_address);
        } else if (place && place.name) {
          // Fallback to place name if formatted_address is not available
          setToLocation(place.name);
        }
        // If neither exists, keep the current input value (user typed text)
      }
    };

    // Calculate distance from a point to the route polyline and its travel distance from start
    const calculateDistanceToRoute = useCallback(
      (lat: number, lng: number, routePath: google.maps.LatLng[]): { perpDistance: number, travelDistance: number } => {
        if (!routePath || routePath.length === 0) return { perpDistance: Infinity, travelDistance: Infinity };

        const numLat = Number(lat);
        const numLng = Number(lng);

        if (isNaN(numLat) || isNaN(numLng)) return { perpDistance: Infinity, travelDistance: Infinity };

        let minPerpDistance = Infinity;
        let travelDistanceAtMinPerp = 0;
        let cumulativeRouteDistance = 0;
        
        const point = new google.maps.LatLng(numLat, numLng);

        // Treat lat/lng as a local 2D plane for projection
        const latScale = 1;
        const lngScale = Math.cos((numLat * Math.PI) / 180);

        const px = numLng * lngScale;
        const py = numLat * latScale;

        // Check distance to each segment of the route
        for (let i = 0; i < routePath.length - 1; i++) {
          const v = routePath[i];
          const w = routePath[i + 1];
          
          // Calculate segment length in meters
          const segmentDist = google.maps.geometry.spherical.computeDistanceBetween(v, w);

          const vx = v.lng() * lngScale;
          const vy = v.lat() * latScale;
          const wx = w.lng() * lngScale;
          const wy = w.lat() * latScale;

          const l2 = (wx - vx) ** 2 + (wy - vy) ** 2;
          let closestLng, closestLat;
          let t = 0;

          if (l2 === 0) {
            closestLng = v.lng();
            closestLat = v.lat();
          } else {
            t = ((px - vx) * (wx - vx) + (py - vy) * (wy - vy)) / l2;
            t = Math.max(0, Math.min(1, t));
            closestLng = v.lng() + t * (w.lng() - v.lng());
            closestLat = v.lat() + t * (w.lat() - v.lat());
          }

          const closestPoint = new google.maps.LatLng(closestLat, closestLng);
          
          try {
            const distance = google.maps.geometry.spherical.computeDistanceBetween(
              point,
              closestPoint
            );

            if (distance < minPerpDistance) {
              minPerpDistance = distance;
              // travelDistance is cumulative distance to segment start + distance along segment to closest point
              travelDistanceAtMinPerp = cumulativeRouteDistance + (t * segmentDist);
            }
          } catch (e) {
             console.error("Error computing distance", e);
          }
          
          cumulativeRouteDistance += segmentDist;
        }

        // Return distances in kilometers
        return { 
          perpDistance: minPerpDistance / 1000, 
          travelDistance: travelDistanceAtMinPerp / 1000 
        };
      },
      []
    );

    // Find restaurants near the route
    const findNearbyRestaurants = useCallback(
      (route: google.maps.DirectionsResult) => {
        if (!restaurants || restaurants.length === 0) return;

        const routePath = route.routes[0]?.overview_path;
        if (!routePath) return;

        const nearby: Array<{ id: string; distance: number }> = [];
        const distanceMap = new Map<string, number>();

        restaurants.forEach((restaurant) => {
          if (
            restaurant.storeLat === 0 ||
            restaurant.storeLong === 0 ||
            restaurant.status !== "Active" ||
            !(restaurant.isHighway === true || restaurant.isHighway === "true")
          )
            return;

          const distanceInfo = calculateDistanceToRoute(
            restaurant.storeLat,
            restaurant.storeLong,
            routePath
          );

          // Store travel distance from start for all restaurants (for UI display and sorting)
          distanceMap.set(restaurant.dbID, distanceInfo.travelDistance);

          // Still use perpendicular distance for filtering (is it actually "along" the route)
          if (distanceInfo.perpDistance <= maxDistanceKm) {
            nearby.push({ id: restaurant.dbID, distance: distanceInfo.travelDistance });
          }
        });

        console.log(
          `Found ${nearby.length} restaurants within ${maxDistanceKm}km of route`
        );

        // Store both IDs and distances
        const nearbyIds = nearby.map((item) => item.id);
        setNearbyRestaurants(nearbyIds);

        if (onNearbyRestaurantsFound) {
          onNearbyRestaurantsFound(nearbyIds);
        }

        // Pass distance map to parent
        if (onRouteDistancesCalculated) {
          onRouteDistancesCalculated(distanceMap);
        }
      },
      [
        restaurants,
        calculateDistanceToRoute,
        onNearbyRestaurantsFound,
        onRouteDistancesCalculated,
      ]
    );

    const calculateRoute = useCallback(async () => {
      if (!fromLocation || !toLocation) {
        setError("Please enter both locations");
        return;
      }

      if (!map) {
        setError("Map not loaded");
        return;
      }

      // Check if Google Maps API is loaded
      if (
        typeof google === "undefined" ||
        !google.maps ||
        !google.maps.DirectionsService
      ) {
        setError("Google Maps API not fully loaded. Please wait and try again.");
        return;
      }

      setIsCalculating(true);
      setError(null);

      try {
        const directionsService = new google.maps.DirectionsService();

        console.log("Calculating route from:", fromLocation, "to:", toLocation);

        const results = await directionsService.route({
          origin: fromLocation,
          destination: toLocation,
          travelMode: google.maps.TravelMode.DRIVING,
        });

        console.log("Route calculation successful:", results);

        setDirectionsResponse(results);
        setRouteKey(Date.now()); // Force re-render with new key
        setShowRoute(true); // Show the route

        // Manually create and display the route
        if (map) {
          // Clear any existing renderer
          if (directionsRendererRef.current) {
            directionsRendererRef.current.setMap(null);
          }

          // Create new renderer
          const renderer = new google.maps.DirectionsRenderer({
            map: map,
            directions: results,
            polylineOptions: {
              strokeColor: "#6366f1",
              strokeWeight: 6,
              strokeOpacity: 0.9,
            },
            suppressMarkers: false,
            markerOptions: {
              zIndex: 100,
            },
          });

          directionsRendererRef.current = renderer;
          console.log("DirectionsRenderer created and displayed");
        }

        // Extract distance and duration
        const route = results.routes[0];
        if (route && route.legs[0]) {
          const leg = route.legs[0];
          const dist = leg.distance?.text || "";
          const dur = leg.duration?.text || "";
          setDistance(dist);
          setDuration(dur);

          console.log("Distance:", dist, "Duration:", dur);

          if (onRouteCalculated) {
            onRouteCalculated(dist, dur);
          }

          // Find restaurants near the route
          findNearbyRestaurants(results);

          // Close the panel after successfully calculating the route
          setTimeout(() => {
            setIsOpen(false);
          }, 500); // Small delay to show the distance/duration before closing
        }
      } catch (err: any) {
        console.error("Error calculating route:", err);

        let errorMessage =
          "Could not calculate route. Please check the locations.";

        // Check for DirectionsStatus errors
        if (err === "ZERO_RESULTS") {
          errorMessage = "No route found between these locations.";
        } else if (err === "NOT_FOUND") {
          errorMessage = "One or both locations could not be found.";
        } else if (err === "REQUEST_DENIED") {
          errorMessage =
            "Directions API access denied. Please check your API key has Directions API enabled.";
        } else if (err === "OVER_QUERY_LIMIT") {
          errorMessage = "Too many requests. Please try again later.";
        } else if (err === "INVALID_REQUEST") {
          errorMessage = "Invalid request. Please check your locations.";
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setDirectionsResponse(null);
      } finally {
        setIsCalculating(false);
      }
    }, [fromLocation, toLocation, map, onRouteCalculated, findNearbyRestaurants]);

    useEffect(() => {
      if (initialRouteDestination && fromLocation && toLocation && !hasAutoCalculated && map) {
        setHasAutoCalculated(true);
        calculateRoute();
        setIsOpen(true); // Open the panel to show it's navigating
      }
    }, [initialRouteDestination, fromLocation, toLocation, hasAutoCalculated, map, calculateRoute]);

    const clearRoute = useCallback(() => {
      console.log("=== CLEARING ROUTE ===");
      console.log("Has directionsRenderer ref:", !!directionsRendererRef.current);

      // First hide the route to trigger unmount
      setShowRoute(false);

      // Manually clear the directions renderer - MORE AGGRESSIVE
      if (directionsRendererRef.current) {
        console.log("Manually clearing DirectionsRenderer");
        try {
          // Set to null first to remove the route
          directionsRendererRef.current.setDirections(null as any);
          // Remove from map
          directionsRendererRef.current.setMap(null);
          // Clear the panel if it exists
          directionsRendererRef.current.setPanel(null);
        } catch (error) {
          console.error("Error clearing directions:", error);
        }
        directionsRendererRef.current = null;
      }

      console.log("Setting states to null/empty");
      setDirectionsResponse(null);
      setFromLocation("");
      setToLocation("");
      setDistance("");
      setDuration("");
      setError(null);
      setNearbyRestaurants([]);
      setRouteKey(0); // Reset key to force unmount

      console.log("Notifying parent about cleared restaurants");
      if (onNearbyRestaurantsFound) {
        onNearbyRestaurantsFound([]);
      }

      // Clear route distances
      if (onRouteDistancesCalculated) {
        onRouteDistancesCalculated(new Map());
      }

      console.log("=== ROUTE CLEARED ===");
    }, [onNearbyRestaurantsFound, onRouteDistancesCalculated]);

    const togglePanel = () => {
      setIsOpen(!isOpen);
      // Clear error when toggling
      setError(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        calculateRoute();
      }
    };

    // Handle click outside to close panel
    // React.useEffect(() => {
    //   const handleClickOutside = (event: MouseEvent) => {
    //     if (
    //       isOpen &&
    //       panelRef.current &&
    //       !panelRef.current.contains(event.target as Node)
    //     ) {
    //       // Check if click is not on the toggle button
    //       const target = event.target as HTMLElement;
    //       if (!target.closest('[aria-label="Get directions"]')) {
    //         setIsOpen(false);
    //       }
    //     }
    //   };

    //   if (isOpen) {
    //     document.addEventListener("mousedown", handleClickOutside);
    //   }

    //   return () => {
    //     document.removeEventListener("mousedown", handleClickOutside);
    //   };
    // }, [isOpen]);

    // Ensure route is hidden when showRoute is false
    useEffect(() => {
      if (!showRoute && directionsRendererRef.current) {
        console.log("Forcing route cleanup because showRoute is false");
        try {
          directionsRendererRef.current.setDirections(null as any);
          directionsRendererRef.current.setMap(null);
          directionsRendererRef.current.setPanel(null);
          directionsRendererRef.current = null;
        } catch (error) {
          console.error("Error in cleanup effect:", error);
        }
      }
    }, [showRoute]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (directionsRendererRef.current) {
          console.log("Component unmounting, cleaning up DirectionsRenderer");
          directionsRendererRef.current.setMap(null);
          directionsRendererRef.current = null;
        }
      };
    }, []);

    return (
      <>
        {/* Toggle Button and Clear Route Button */}
        <div className="absolute top-4 left-4 z-10 flex gap-2">
          <button
            onClick={togglePanel}
            className="group bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white px-4 py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 text-sm font-semibold backdrop-blur-sm border border-indigo-400/20 transform hover:scale-105"
            aria-label="Get directions"
          >
            <svg
              className="w-5 h-5 transition-transform group-hover:rotate-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
            <span className="hidden sm:inline">Directions</span>
          </button>

          {/* Clear Route Button - Only show when route exists and panel is closed */}
          {directionsResponse && !isOpen && (
            <button
              onClick={clearRoute}
              className="group bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white px-4 py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 text-sm font-semibold backdrop-blur-sm border border-rose-400/20 transform hover:scale-105"
              aria-label="Clear route"
            >
              <svg
                className="w-5 h-5 transition-transform group-hover:rotate-90"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </div>

        {/* Route Input Panel */}
        {isOpen && (
          <div
            ref={panelRef}
            className="absolute top-16 left-4 right-4 sm:right-auto z-10 bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl sm:w-96 border border-gray-700/50 max-w-sm animate-in slide-in-from-top-5 duration-300 max-h-[70vh] flex flex-col"
          >
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 rounded-2xl pointer-events-none"></div>

            {/* <div className="relative flex-shrink-0 p-4 sm:p-5 pb-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                </div>
                <h3 className="text-white font-bold text-lg bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Get Directions
                </h3>
              </div>
              <button
                onClick={togglePanel}
                className="text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200 p-2 rounded-lg"
                aria-label="Close directions panel"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div> */}

            {/* Scrollable Content */}
            <div
              className=" pt-4 relative overflow-y-auto overflow-x-hidden px-4 sm:px-5  sm:pb-5 scrollbar-thin scrollbar-thumb-indigo-500/50 scrollbar-track-gray-700/30 hover:scrollbar-thumb-indigo-500/70"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(99, 102, 241, 0.5) rgba(55, 65, 81, 0.3)",
              }}
            >
              {/* From Input with Autocomplete */}
              <div className="mb-3">
                <label className="text-gray-300 text-sm font-medium mb-2 flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <svg
                      className="w-4 h-4 text-green-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <circle cx="10" cy="10" r="4" />
                    </svg>
                    Starting Point
                  </span>
                  <button
                    onClick={() => {
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (position) => {
                            const lat = position.coords.latitude;
                            const lng = position.coords.longitude;
                            setFromLocation(`${lat},${lng}`);
                          },
                          (err) => {
                            console.error("Error getting location:", err);
                            setError("Could not get your location");
                          }
                        );
                      }
                    }}
                    className="text-xs text-indigo-400 hover:text-indigo-300 font-medium flex items-center gap-1 transition-colors"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    My Location
                  </button>
                </label>
                <Autocomplete
                  onLoad={onFromLoad}
                  onPlaceChanged={onFromPlaceChanged}
                  options={{
                    componentRestrictions: { country: "in" },
                  }}
                  className=""
                >
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={fromLocation}
                      onChange={(e) => setFromLocation(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Enter starting location"
                      className="w-full pl-10 pr-4 py-3 text-sm bg-gray-800/50 text-white rounded-xl border border-gray-600/50 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:bg-gray-800/70 transition-all duration-200 placeholder:text-gray-500 hover:border-gray-500/70"
                    />
                  </div>
                </Autocomplete>
              </div>

              {/* To Input with Autocomplete */}
              <div className="mb-4">
                <label className="text-gray-300 text-sm font-medium mb-2 flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4 text-red-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Destination
                </label>
                <Autocomplete
                  onLoad={onToLoad}
                  onPlaceChanged={onToPlaceChanged}
                  options={{
                    componentRestrictions: { country: "in" },
                  }}
                >
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={toLocation}
                      onChange={(e) => setToLocation(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Enter destination"
                      className="w-full pl-10 pr-4 py-3 text-sm bg-gray-800/50 text-white rounded-xl border border-gray-600/50 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:bg-gray-800/70 transition-all duration-200 placeholder:text-gray-500 hover:border-gray-500/70"
                    />
                  </div>
                </Autocomplete>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={calculateRoute}
                  disabled={isCalculating || !fromLocation || !toLocation}
                  className="flex-1 px-4 py-3 text-sm bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-600 disabled:from-gray-700 disabled:to-gray-600 disabled:cursor-not-allowed transition-all duration-300 font-semibold shadow-lg hover:shadow-xl disabled:shadow-none flex items-center justify-center gap-2 transform hover:scale-[1.02] disabled:transform-none"
                >
                  {isCalculating ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Calculating...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                      Find Restaurants
                    </>
                  )}
                </button>
                {directionsResponse && (
                  <button
                    onClick={clearRoute}
                    className="px-4 py-3 text-sm bg-gradient-to-r from-rose-600 to-red-600 text-white rounded-xl hover:from-rose-700 hover:to-red-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2 transform hover:scale-[1.02]"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Clear
                  </button>
                )}
              </div>

              {/* Error Message */}
              {/* {error && (
              <div className="bg-gradient-to-r from-red-900/40 to-rose-900/40 border border-red-500/50 text-red-300 px-4 py-3 rounded-xl text-sm mb-4 flex items-start gap-2 backdrop-blur-sm animate-in slide-in-from-top-2 duration-300">
                <svg
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )} */}

              {/* Route Info */}
              {/* {directionsResponse && distance && duration && (
              <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 rounded-xl p-4 border border-gray-600/50 space-y-3 backdrop-blur-sm animate-in fade-in-50 duration-500">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-lg border border-blue-500/20">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-blue-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">
                      Distance
                    </span>
                  </div>
                  <span className="text-white font-bold text-base bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    {distance}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-purple-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">
                      Duration
                    </span>
                  </div>
                  <span className="text-white font-bold text-base bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    {duration}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/20">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-amber-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                        />
                      </svg>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">
                      Restaurants Nearby
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-base bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                      {nearbyRestaurants.length}
                    </span>
                    <span className="text-xs text-gray-400 bg-gray-700/50 px-2 py-1 rounded-full">
                      within 10km
                    </span>
                  </div>
                </div>
              </div>
            )} */}
            </div>
          </div>
        )}

        {/* DirectionsRenderer is now managed manually via the Google Maps API */}
      </>
    );
  };

export default RouteDirectionsWithAutocomplete;
